import { useEffect, useMemo, useRef } from 'react';
import { useMatches } from 'react-router';

import { trackAnalyticsPage } from '~/common/analytics/analyticsClient';
import { getPathTemplate } from '~/common/analytics/routeTemplate';
import {
  buildSegmentIdentity,
  isAutomationCustomAiPath,
} from '~/common/analytics/segmentIdentity';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useRouting } from '~/routing/useRouting';

type PageLocation = {
  path: string;
  url: string;
};

function resolveCurrentPage(
  pathname: string,
  params: Record<string, string | undefined>,
): PageLocation {
  return {
    path: getPathTemplate(pathname, params),
    url:
      globalThis.location === undefined ? pathname : globalThis.location.href,
  };
}

export const useTrackPage = () => {
  const { location } = useRouting();
  const matches = useMatches();
  const user = useCurrentUser();
  const userId = user?.id;
  const clientId = user?.client;
  const clientName = user?.client_name;

  const identity = useMemo(
    () => buildSegmentIdentity(userId, clientId, clientName),
    [userId, clientId, clientName],
  );

  const trackedNonAutomationLocationRef = useRef<unknown>(null);
  const previousPageRef = useRef<PageLocation | null>(null);

  useEffect(() => {
    const pathname = location.pathname ?? '';
    const isAutomationCustomAi = isAutomationCustomAiPath(pathname);

    if (!isAutomationCustomAi) {
      if (trackedNonAutomationLocationRef.current === location) return;
      trackedNonAutomationLocationRef.current = location;
    } else {
      trackedNonAutomationLocationRef.current = null;
    }

    const params = matches.at(-1)?.params ?? {};
    const currentPage = resolveCurrentPage(pathname, params);
    const previousPage = previousPageRef.current;
    previousPageRef.current = currentPage;

    const pageProperties = {
      current_path: currentPage.path,
      current_url: currentPage.url,
      previous_path: previousPage?.path ?? null,
      previous_url: previousPage?.url ?? null,
    };

    if (!isAutomationCustomAi) {
      trackAnalyticsPage(pageProperties);
      return;
    }

    trackAnalyticsPage({
      ...identity,
      ...pageProperties,
      product_area: 'custom_ai',
    });
  }, [location, identity, matches]);
};
